import { createIsomorphicFn } from "@tanstack/react-start";
import { loadPublicSiteSettings } from "@/lib/site-settings.functions";
import { buildThemeStylesheet } from "@/lib/theme-settings";
import { buildHomeThemeDetailsStylesheet } from "@/lib/home-theme-details";

/**
 * Admin-managed theme CSS (Admin → Theme and Home visual details).
 *
 * It used to be loaded as `<link rel="stylesheet" href="/theme.css">`: a
 * render-blocking request to a route that queries Supabase on every isolate
 * cache miss (measured 0.2s to 1.1s TTFB in production), which sat in front
 * of first paint and LCP on every public page. The stylesheet is now inlined
 * into the SSR document at the same cascade position. The `/theme.css` route
 * stays available for anything that still links to it.
 */
export const PUBLIC_THEME_STYLE_ID = "shugert-dynamic-theme";
const THEME_TTL_MS = 60 * 1000;

let cachedCss: { css: string; expiresAt: number } | null = null;
let pending: Promise<string> | null = null;

export async function buildPublicThemeCss(): Promise<string> {
  const settings = await loadPublicSiteSettings();
  return [
    buildThemeStylesheet(settings.theme_colors),
    buildHomeThemeDetailsStylesheet(settings.theme_colors.home_details),
  ]
    .join("\n")
    .replace(/<\/style/gi, "<\\/style");
}

/** Server: refresh the per-isolate copy (60s, like the old edge TTL). Client: no-op. */
export const preparePublicThemeCss = createIsomorphicFn()
  .client(async () => {})
  .server(async () => {
    if (cachedCss && cachedCss.expiresAt > Date.now()) return;
    if (!pending) {
      pending = buildPublicThemeCss()
        .then((css) => {
          cachedCss = { css, expiresAt: Date.now() + THEME_TTL_MS };
          return css;
        })
        .finally(() => {
          pending = null;
        });
    }
    try {
      await pending;
    } catch (error) {
      // A stale copy, or the /theme.css link fallback, still styles the page.
      console.warn("[theme-css] refresh failed", error);
    }
  });

/**
 * Server: the prepared CSS, or null when it could not be built.
 * Client: the CSS the server inlined, so hydration and later renders keep the
 * exact same `<style>` contents.
 */
export const readPublicThemeCss = createIsomorphicFn()
  .client((): string | null => document.getElementById(PUBLIC_THEME_STYLE_ID)?.textContent ?? null)
  .server((): string | null => cachedCss?.css ?? null);
